import { Loader2, type LucideIcon } from 'lucide-react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { present, type StatusPresentation } from '@/utils/labels';

/**
 * The interface primitives (TZ section 14).
 *
 * Screens compose these and nothing else. Every size, radius and colour here
 * comes from a token in styles.css, so there is one button height per size and
 * one corner radius per shape across the whole panel, by construction rather
 * than by review.
 */

type ButtonVariant = 'default' | 'primary' | 'ghost' | 'danger' | 'danger-solid';
type ControlSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ControlSize;
  readonly icon?: LucideIcon;
  readonly block?: boolean;
  /** Shows a spinner and disables the button. The label stays, so the button
   *  does not change width and move everything next to it. */
  readonly busy?: boolean;
}

const ICON_SIZE: Record<ControlSize, number> = { sm: 14, md: 16, lg: 18 };

export function Button({
  variant = 'default',
  size = 'md',
  icon: Icon,
  block = false,
  busy = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): ReactNode {
  return (
    <button {...rest} type={type} className={buttonClasses(variant, size, block, className)} disabled={disabled === true || busy}>
      {busy ? (
        <Loader2 size={ICON_SIZE[size]} className="spin" aria-hidden="true" />
      ) : Icon ? (
        <Icon size={ICON_SIZE[size]} aria-hidden="true" />
      ) : null}
      {children}
    </button>
  );
}

function buttonClasses(variant: ButtonVariant, size: ControlSize, block: boolean, className: string | undefined): string {
  return ['btn', variant === 'default' ? '' : `btn-${variant}`, size === 'md' ? '' : `btn-${size}`, block ? 'btn-block' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
}

/**
 * A link that looks like a button: for going somewhere else — a site's admin,
 * a download — which a button would have to fake with script. Always a new tab,
 * never with this page as its opener.
 */
export function ButtonLink({
  href,
  variant = 'default',
  size = 'md',
  icon: Icon,
  download,
  children,
}: {
  readonly href: string;
  readonly variant?: ButtonVariant;
  readonly size?: ControlSize;
  readonly icon?: LucideIcon;
  /** Saved rather than opened. */
  readonly download?: boolean;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <a
      className={buttonClasses(variant, size, false, undefined)}
      href={href}
      {...(download ? { download: '' } : { target: '_blank', rel: 'noreferrer noopener' })}
    >
      {Icon ? <Icon size={ICON_SIZE[size]} aria-hidden="true" /> : null}
      {children}
    </a>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children' | 'block'> {
  readonly icon: LucideIcon;
  /** Required: an icon on its own says nothing to a screen reader, and little
   *  enough to everyone else. */
  readonly label: string;
}

export function IconButton({ icon: Icon, label, size = 'md', className, ...rest }: IconButtonProps): ReactNode {
  return (
    <Button
      {...rest}
      size={size}
      aria-label={label}
      title={label}
      className={['btn-icon', className ?? ''].filter(Boolean).join(' ')}
    >
      <Icon size={ICON_SIZE[size]} aria-hidden="true" />
    </Button>
  );
}

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string;
  readonly htmlFor?: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="field-hint">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly invalid?: boolean;
}

export function Input({ invalid = false, className, ...rest }: InputProps): ReactNode {
  return (
    <input
      {...rest}
      aria-invalid={invalid || undefined}
      className={['input', invalid ? 'input-invalid' : '', className ?? ''].filter(Boolean).join(' ')}
    />
  );
}

export function Card({
  title,
  icon: Icon,
  actions,
  flush = false,
  children,
}: {
  readonly title?: string;
  readonly icon?: LucideIcon;
  readonly actions?: ReactNode;
  /** No padding, for a card whose whole body is a table. */
  readonly flush?: boolean;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className="card">
      {title ? (
        <header className="card-head">
          <h3 className="card-title">
            {Icon ? <Icon size={16} aria-hidden="true" /> : null}
            {title}
          </h3>
          {actions}
        </header>
      ) : null}
      <div className={flush ? 'card-body card-body-flush' : 'card-body'}>{children}</div>
    </section>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly actions?: ReactNode;
}): ReactNode {
  return (
    <header className="page-head">
      <div>
        <h2 className="page-title">{title}</h2>
        {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </header>
  );
}

export function StatusPill({
  map,
  status,
}: {
  readonly map: Record<string, StatusPresentation>;
  readonly status: string;
}): ReactNode {
  const view = present(map, status);
  return (
    <span className={`status status-${view.tone}`} title={view.hint}>
      <span className="status-glyph" aria-hidden="true">
        {view.glyph}
      </span>
      {view.label}
    </span>
  );
}

export function Tag({ children }: { readonly children: ReactNode }): ReactNode {
  return <span className="tag">{children}</span>;
}

export function Banner({
  tone = 'info',
  icon: Icon,
  spin = false,
  children,
}: {
  readonly tone?: 'info' | 'warning' | 'danger';
  readonly icon?: LucideIcon;
  /** For a banner about work in progress: the icon turns while it lasts. */
  readonly spin?: boolean;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div className={`banner banner-${tone}`} role="status">
      {Icon ? <Icon size={18} aria-hidden="true" className={spin ? 'spin' : undefined} /> : null}
      <div>{children}</div>
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  message,
  action,
}: {
  readonly icon?: LucideIcon;
  readonly message: string;
  readonly action?: ReactNode;
}): ReactNode {
  return (
    <div className="state">
      {Icon ? <Icon size={28} aria-hidden="true" /> : null}
      <p>{message}</p>
      {action}
    </div>
  );
}

export function Spinner({ label = 'Загрузка…' }: { readonly label?: string }): ReactNode {
  return (
    <div className="state">
      <Loader2 size={24} className="spin" aria-hidden="true" />
      <p>{label}</p>
    </div>
  );
}

/** Label and value pairs, for any screen that lists properties of a thing. */
export function Facts({ children }: { readonly children: ReactNode }): ReactNode {
  return <dl className="facts">{children}</dl>;
}

export function Fact({
  label,
  icon,
  children,
}: {
  readonly label: string;
  /** A service's or platform's mark beside its name. */
  readonly icon?: ReactNode;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <>
      <dt>{icon ? <span className="row-tight">{icon}{label}</span> : label}</dt>
      <dd>{children}</dd>
    </>
  );
}
